/**
 * State Subscription Context
 *
 * SIMPLIFIED: All users have access to all states.
 * Subscription logic has been removed - keeping the interface for compatibility.
 */

import React, { createContext, useContext } from 'react';

interface StateSubscriptionContextType {
  /** Array of state codes the user is subscribed to */
  subscribedStates: string[];
  /** Check if user has access to a specific state */
  isStateSubscribed: (stateCode: string) => boolean;
  /** Whether user has any state subscriptions */
  hasAnyStateSubscription: boolean;
  /** Loading state while fetching subscriptions */
  loading: boolean;
  /** Force refresh subscriptions from Firestore */
  refreshSubscriptions: () => Promise<void>;
  /** Whether user is a superadmin (bypasses all subscription checks) */
  isSuperadminAccess: boolean;
}

const StateSubscriptionContext = createContext<StateSubscriptionContextType>({
  subscribedStates: ['FL'], // Default to Florida
  isStateSubscribed: () => true, // Always grant access
  hasAnyStateSubscription: true,
  loading: false,
  refreshSubscriptions: async () => {},
  isSuperadminAccess: true, // Treat all users as having full access
});

export const useStateSubscription = () => useContext(StateSubscriptionContext);

interface StateSubscriptionProviderProps {
  children: React.ReactNode;
}

export const StateSubscriptionProvider: React.FC<StateSubscriptionProviderProps> = ({ children }) => {
  // Simplified: all users have access to all states
  const value: StateSubscriptionContextType = {
    subscribedStates: ['FL'],
    isStateSubscribed: () => true, // Always return true
    hasAnyStateSubscription: true,
    loading: false,
    refreshSubscriptions: async () => {},
    isSuperadminAccess: true,
  };

  return (
    <StateSubscriptionContext.Provider value={value}>
      {children}
    </StateSubscriptionContext.Provider>
  );
};
